import React, { useState, useEffect, useCallback } from 'react';
import { API_BASE_URL } from '../../config/api';

function ModerationDashboard() {
  const [contributions, setContributions] = useState([]);
  const [statusFilter, setStatusFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [confidenceFilter, setConfidenceFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [selectedContribution, setSelectedContribution] = useState(null);
  const [actionReason, setActionReason] = useState('');
  const [actionLoading, setActionLoading] = useState({});

  const getAuthHeaders = useCallback(() => {
    const token = localStorage.getItem('admin_token');
    return { 'Authorization': `Bearer ${token}` };
  }, []);

  const fetchContributions = useCallback(async () => {
    setLoading(true);
    try {
      let url = `${API_BASE_URL}/admin/contributions/pending?`;
      if (statusFilter) url += `status=${encodeURIComponent(statusFilter)}&`;
      if (typeFilter) url += `contribution_type=${encodeURIComponent(typeFilter)}&`;
      if (confidenceFilter) url += `confidence=${encodeURIComponent(confidenceFilter)}&`;

      const res = await fetch(url, { headers: getAuthHeaders() });
      if (res.ok) {
        setContributions(await res.json());
      }
    } catch (e) {
      console.error('Failed to fetch contributions', e);
    } finally {
      setLoading(false);
    }
  }, [statusFilter, typeFilter, confidenceFilter, getAuthHeaders]);

  useEffect(() => {
    fetchContributions();
  }, [fetchContributions]);

  const handleAction = async (contribId, action) => {
    setActionLoading(prev => ({ ...prev, [contribId]: true }));
    try {
      const url = `${API_BASE_URL}/admin/contributions/${contribId}/${action}?reason=${encodeURIComponent(actionReason)}`;
      const res = await fetch(url, {
        method: 'POST',
        headers: getAuthHeaders()
      });

      if (!res.ok) {
        const errData = await res.json();
        throw new Error(errData.detail || `Failed to ${action}`);
      }

      alert(`Contribution successfully ${action}ed!`);
      setActionReason('');
      setSelectedContribution(null);
      fetchContributions();
    } catch (e) {
      console.error(`Failed to ${action} contribution`, e);
      alert(`Error: ${e.message}`);
    } finally {
      setActionLoading(prev => ({ ...prev, [contribId]: false }));
    }
  };

  const getPriorityBadgeClass = (type) => {
    switch (type) {
      case 'SAFETY_ALERT':
      case 'ROAD_BLOCK':
      case 'ROUTE_DIVERSION':
      case 'MEDICAL_CAMP':
        return 'badge-danger';
      case 'FOOD_CAMP':
      case 'ACCOMMODATION':
      case 'VOLUNTEER_HELP':
        return 'badge-success';
      default:
        return 'badge-info';
    }
  };

  const getConfidenceBadgeClass = (confidence) => {
    switch (confidence) {
      case 'HIGH': return 'badge-success';
      case 'MEDIUM': return 'badge-info';
      case 'LOW': return 'badge-danger';
      default: return 'badge-info';
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '20px', gap: '12px', flexWrap: 'wrap' }}>
          <h3>Crowdsourced Contributions Queue</h3>
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              style={{ padding: '8px', borderRadius: '6px', border: '1px solid #ddd' }}
            >
              <option value="">Pending / Under Review</option>
              <option value="PENDING">PENDING</option>
              <option value="UNDER_REVIEW">UNDER_REVIEW</option>
              <option value="VERIFIED">VERIFIED</option>
              <option value="REJECTED">REJECTED</option>
              <option value="ARCHIVED">ARCHIVED</option>
            </select>

            <select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              style={{ padding: '8px', borderRadius: '6px', border: '1px solid #ddd' }}
            >
              <option value="">All Types</option>
              <option value="SAFETY_ALERT">Safety Alert</option>
              <option value="ROAD_BLOCK">Road Block</option>
              <option value="ROUTE_DIVERSION">Route Diversion</option>
              <option value="MEDICAL_CAMP">Medical Camp</option>
              <option value="FOOD_CAMP">Food Camp</option>
              <option value="WATER_POINT">Water Point</option>
              <option value="ACCOMMODATION">Accommodation</option>
              <option value="REST_POINT">Rest Point</option>
            </select>

            <select
              value={confidenceFilter}
              onChange={(e) => setConfidenceFilter(e.target.value)}
              style={{ padding: '8px', borderRadius: '6px', border: '1px solid #ddd' }}
            >
              <option value="">All Confidences</option>
              <option value="HIGH">High Confidence</option>
              <option value="MEDIUM">Medium Confidence</option>
              <option value="LOW">Low Confidence</option>
            </select>

            <button className="btn-outline" onClick={fetchContributions}>Refresh</button>
          </div>
        </div>

        {loading ? (
          <p>Loading contributions...</p>
        ) : (
          <table className="admin-table">
            <thead>
              <tr>
                <th>Title</th>
                <th>Type</th>
                <th>Confidence</th>
                <th>Score</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {contributions.map((c) => (
                <tr key={c.id}>
                  <td>
                    <strong>{c.title}</strong>
                    <div style={{ fontSize: '12px', color: '#666' }}>{c.description?.slice(0, 50)}...</div>
                  </td>
                  <td>
                    <span className={getPriorityBadgeClass(c.contribution_type)}>
                      {c.contribution_type}
                    </span>
                  </td>
                  <td>
                    <span className={getConfidenceBadgeClass(c.location_confidence)}>
                      {c.location_confidence || 'LOW'}
                    </span>
                  </td>
                  <td>{c.verification_score}</td>
                  <td>
                    <span className="badge-info">{c.status}</span>
                  </td>
                  <td>
                    <button 
                      className="btn-outline" 
                      style={{ marginRight: '8px' }}
                      onClick={() => {
                        setSelectedContribution(c);
                        setActionReason('');
                      }}
                    >
                      Review
                    </button>
                  </td>
                </tr>
              ))}
              {contributions.length === 0 && (
                <tr><td colSpan="6" style={{ textAlign: 'center', padding: '20px' }}>No contributions waiting in this queue.</td></tr>
              )}
            </tbody>
          </table>
        )}
      </div>

      {selectedContribution && (
        <div className="card" style={{ border: '2px solid var(--primary)' }}>
          <h3>Review Contribution: {selectedContribution.title}</h3>
          <p style={{ marginTop: '8px', color: '#555' }}>{selectedContribution.description}</p>
          
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginTop: '16px' }}>
            <div>
              <h4>Details</h4>
              <ul style={{ listStyleType: 'none', padding: 0, marginTop: '8px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
                <li><strong>Type:</strong> {selectedContribution.contribution_type}</li>
                <li><strong>Location:</strong> {selectedContribution.latitude}, {selectedContribution.longitude}</li>
                <li><strong>Status:</strong> {selectedContribution.status}</li>
                <li><strong>Verification Score:</strong> {selectedContribution.verification_score}</li>
                <li><strong>Visibility radius:</strong> {selectedContribution.visibility_radius_meters || 500}m</li>
              </ul>
            </div>
            {selectedContribution.photo_url && (
              <div>
                <h4>Evidence Photo</h4>
                <img 
                  src={selectedContribution.photo_url} 
                  alt="Evidence" 
                  style={{ width: '100%', maxHeight: '200px', objectFit: 'contain', marginTop: '8px', borderRadius: '4px', border: '1px solid #ddd' }}
                  onError={(e) => { e.target.style.display = 'none'; }}
                />
              </div>
            )}
          </div>

          <div style={{ marginTop: '24px' }}>
            <h4>Moderation Action Details</h4>
            <input
              type="text"
              placeholder="Provide reason for verification, rejection, or promotion..."
              value={actionReason}
              onChange={(e) => setActionReason(e.target.value)}
              style={{ width: '100%', padding: '10px', marginTop: '8px', borderRadius: '6px', border: '1px solid #ccc' }}
            />
            
            <div style={{ display: 'flex', gap: '12px', marginTop: '16px' }}>
              <button 
                className="btn-primary"
                disabled={actionLoading[selectedContribution.id]}
                onClick={() => handleAction(selectedContribution.id, 'verify')}
              >
                Verify Manual
              </button>
              <button 
                className="btn-outline" 
                style={{ color: 'red', borderColor: 'red' }}
                disabled={actionLoading[selectedContribution.id]}
                onClick={() => handleAction(selectedContribution.id, 'reject')}
              >
                Reject Manual
              </button>
              <button 
                className="btn-primary" 
                style={{ background: '#4CAF50' }}
                disabled={actionLoading[selectedContribution.id]}
                onClick={() => handleAction(selectedContribution.id, 'promote')}
              >
                Promote to Official Facility
              </button>
              <button 
                className="btn-outline" 
                onClick={() => setSelectedContribution(null)}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ModerationDashboard;
